import React, { useState } from 'react';
import { LuPlus as Plus, LuSearch as Search, LuTrash2 as Trash } from 'react-icons/lu';
import { FaExclamationTriangle as TriangleAlert } from 'react-icons/fa';

import { Field, Notice } from './formPrimitives';
import {
  formatDate,
  fetchListByChart,
  deletePatientFromList
} from './listApi';
import { getSessionUser } from './v2Helpers';

function eyeLabel(eye) {
  if (eye === 'OU') return 'OD · OS';
  return eye || '—';
}

export function createDefaultListQuery() {
  const endDate = new Date();
  const startDate = new Date(endDate);
  startDate.setMonth(startDate.getMonth() - 3);
  return {
    chartNo: '',
    startDt: formatDate(startDate),
    endDt: formatDate(endDate)
  };
}

/**
 * V2 List 화면
 * - 차트번호는 선택, 기간으로 조회
 * - 「새 렌즈 추천」으로 create 진입
 * - 행 클릭으로 detail 진입
 * - 행 우측 삭제로 해당 case_id 이력 삭제 (확인 팝업)
 * - query/rows/error 는 상위에서 유지해 상세 왕복 시 초기화되지 않는다.
 */
export default function LensRecommendListFrame({
  query,
  onQueryChange,
  rows,
  onRowsChange,
  error,
  onErrorChange,
  searched = false,
  onSearchedChange,
  onCreate,
  onOpenDetail
}) {
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const updateQuery = (key, value) => {
    onQueryChange((current) => ({ ...current, [key]: value }));
    onErrorChange('');
  };

  const runSearch = async () => {
    const chartNo = query.chartNo.trim();
    if (!query.startDt || !query.endDt) {
      onErrorChange('조회 시작일과 종료일을 입력해주세요.');
      return;
    }
    if (query.startDt > query.endDt) {
      onErrorChange('시작일은 종료일보다 늦을 수 없습니다.');
      return;
    }

    const user = getSessionUser();
    if (!user?.hospitalId) {
      onErrorChange('병원 정보가 없어 조회할 수 없습니다.');
      return;
    }

    setLoading(true);
    onErrorChange('');
    try {
      // 렌즈추천 히스토리 조회
      const listRows = await fetchListByChart({
        chartNo,
        hospitalId: user.hospitalId,
        startDt: query.startDt,
        endDt: query.endDt
      });
      onRowsChange(listRows);
      onSearchedChange?.(true);
    } catch (err) {
      onRowsChange([]);
      onSearchedChange?.(true);
      onErrorChange(err instanceof Error ? err.message : '조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteConfirm = (event, row) => {
    event.stopPropagation();
    setDeleteTarget(row);
    onErrorChange('');
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const user = getSessionUser();
    if (!user?.hospitalId) {
      onErrorChange('병원 정보가 없어 삭제할 수 없습니다.');
      setDeleteTarget(null);
      return;
    }

    setDeleting(true);
    try {
      await deletePatientFromList({
        caseId: deleteTarget.caseId,
        chartNo: deleteTarget.chartNo,
        eye: deleteTarget.eye,
        hospitalId: user.hospitalId
      });
      onRowsChange(rows.filter((row) => row.key !== deleteTarget.key));
      setDeleteTarget(null);
      onErrorChange(
        `chart_no ${deleteTarget.chartNo} · Eye ${deleteTarget.eye} 케이스 이력을 삭제했습니다.`
      );
    } catch (err) {
      onErrorChange(err instanceof Error ? err.message : '삭제에 실패했습니다.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="lens-page">
      <header className="lens-header">
        <div>
          <h1 className="lens-title">ICL Vault AI</h1>
          {/* <p className="lens-subtitle">환자 검색 · 새 렌즈 추천 · 상세 관리 (v2)</p> */}
          <p className="lens-subtitle">검사값 기반 최적 렌즈 추천</p>
        </div>
      </header>

      <div className="lens-v2-toolbar">
        <div className="lens-v2-search-card">
          <div className="lens-v2-search-grid">
            <Field
              label="차트번호"
              value={query.chartNo}
              onChangeText={(value) => updateQuery('chartNo', value)}
              placeholder="차트번호 입력"
            />
            <Field
              label="시작일"
              type="date"
              value={query.startDt}
              onChangeText={(value) => updateQuery('startDt', value)}
            />
            <Field
              label="종료일"
              type="date"
              value={query.endDt}
              onChangeText={(value) => updateQuery('endDt', value)}
            />
            <div className="lens-v2-search-actions">
              <button className="lens-v2-secondary-btn" type="button" onClick={runSearch} disabled={loading}>
                <Search size={15} />
                {loading ? '조회 중' : '조회'}
              </button>
            </div>
          </div>
        </div>
        <button className="lens-v2-primary-btn lens-v2-create-btn" type="button" onClick={onCreate}>
          <Plus size={17} />
          새 렌즈 추천
        </button>
      </div>

      {error ? (
        <Notice tone={/삭제했습니다/.test(error) ? 'info' : 'bad'} icon={<TriangleAlert size={15} color="#b45309" />}>
          {error}
        </Notice>
      ) : null}

      <section className="lens-v2-list-card">
        <div className="lens-v2-history-title">
          <h3>환자 List</h3>
          <span className="lens-v2-pill">{rows.length}건</span>
        </div>
        <div className="lens-v2-table-wrap">
          {rows.length ? (
            <table className="lens-v2-table lens-v2-table-clickable">
              <thead>
                <tr>
                  <th>차트번호</th>
                  <th>생년월일</th>
                  <th>나이</th>
                  <th>Eye</th>
                  <th>최근 기록일</th>
                  <th className="lens-v2-col-action"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.key}
                    onClick={() => onOpenDetail({
                      chartNo: row.chartNo,
                      eye: row.eye,
                      caseId: row.caseId,
                      seedRow: row.raw
                    })}
                  >
                    <td>{row.chartNo}</td>
                    <td>{row.birthDate || '—'}</td>
                    <td>
                      {row.ageYears !== '' && row.ageYears != null
                        ? `${row.ageYears}세`
                        : '—'}
                    </td>
                    <td>{eyeLabel(row.eye)}</td>
                    <td>{row.lastDt || '—'}</td>
                    <td className="lens-v2-col-action">
                      <button
                        className="lens-v2-delete-btn"
                        type="button"
                        aria-label={`chart_no ${row.chartNo} Eye ${row.eye} 삭제`}
                        title="삭제"
                        onClick={(event) => openDeleteConfirm(event, row)}
                      >
                        <Trash size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="lens-v2-empty">
              {loading
                ? '조회 중…'
                : searched
                  ? '조회 결과가 없습니다.「새 렌즈 추천」으로 등록할 수 있습니다.'
                  : '차트번호로 조회하거나 「새 렌즈 추천」으로 시작하세요.'}
            </div>
          )}
        </div>
      </section>

      {deleteTarget ? (
        <div className="lens-v2-confirm-backdrop" role="presentation" onClick={closeDeleteConfirm}>
          <div
            className="lens-v2-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lens-v2-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="lens-v2-confirm-icon">
              <TriangleAlert size={22} color="#b45309" />
            </div>
            <h3 id="lens-v2-delete-title">케이스 이력을 삭제할까요?</h3>
            <p>
              <strong>chart_no {deleteTarget.chartNo} · Eye {deleteTarget.eye}</strong>
              의 해당 케이스(렌즈추천·경과관찰) 이력이 삭제 처리됩니다.
              <br />
              같은 차트·Eye의 다른 케이스(재수술 등)는 유지됩니다.
            </p>
            <div className="lens-v2-confirm-actions">
              <button
                className="lens-v2-secondary-btn"
                type="button"
                onClick={closeDeleteConfirm}
                disabled={deleting}
              >
                취소
              </button>
              <button
                className="lens-v2-danger-btn"
                type="button"
                onClick={confirmDelete}
                disabled={deleting}
              >
                {deleting ? '삭제 중…' : '삭제'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
